var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Flex, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid_v4 } from 'uuid';
import { BlockStyle, Button, ConfigBlockListItem, ConfigInlineHeader, ConfigSchedule, Image, MarkdownBase, NavLink } from '@/components';
import { useNmibleStatus, usePolling } from '@/hooks';
import { SchedulingService } from '@/services';
import { setUserPhase, useAuth, useConfig } from '@/store';
import { APP_CONFIG, ROUTE } from '@/types';
import { resolveWebViewUrl } from '@/utils';
const { ConfigBlockType, ConfigComponentType } = APP_CONFIG.COMPONENTS;
export const Dashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getComponentConfig, getImageConfig, getNavigationDestinationConfig, getStyleConfig } = useConfig();
    const { dispatch, state: { currentUserPhase } } = useAuth();
    const dashboard = getComponentConfig(ConfigComponentType.DASHBOARD, currentUserPhase);
    const dashboardWeb = getComponentConfig(ConfigComponentType.WEB_DASHBOARD);
    const scheduleQuery = getComponentConfig(ConfigComponentType.SCHEDULE_QUERY);
    const pollingCallback = () => __awaiter(void 0, void 0, void 0, function* () {
        const query = {
            after_date: (scheduleQuery === null || scheduleQuery === void 0 ? void 0 : scheduleQuery.after_date) || '',
            before_date: (scheduleQuery === null || scheduleQuery === void 0 ? void 0 : scheduleQuery.before_date) || ''
        };
        const phase = yield SchedulingService.getCurrentUserPhaseAndEvents(query);
        dispatch(setUserPhase(phase));
        return phase;
    });
    usePolling(pollingCallback, { deps: [scheduleQuery, dispatch] });
    const findConfigBlock = (type) => {
        return dashboard === null || dashboard === void 0 ? void 0 : dashboard.blocks.find((b) => b.type === type);
    };
    const blocks = useMemo(() => {
        const list = findConfigBlock(ConfigBlockType.LIST);
        const header = findConfigBlock(ConfigBlockType.HEADER);
        const logo = findConfigBlock(ConfigBlockType.IMAGE);
        const information = findConfigBlock(ConfigBlockType.INFORMATION);
        const paymentInfo = findConfigBlock(ConfigBlockType.PAYMENT_INFO);
        const schedule = findConfigBlock(ConfigBlockType.SCHEDULE);
        return {
            information,
            list: {
                header,
                items: list === null || list === void 0 ? void 0 : list.items.filter((x) => !x.phases || x.phases.includes(currentUserPhase))
            },
            logo,
            paymentInfo,
            schedule
        };
    }, [dashboard, currentUserPhase]);
    const { nmibleStatus } = useNmibleStatus({ enabled: !!blocks.paymentInfo });
    const StudyLogo = useMemo(() => {
        if (!blocks.logo)
            return null;
        return _jsx(Image, { alt: 'logo', h: '125px', image_id: blocks.logo.image_id, mb: '20px', w: 'auto' });
    }, [blocks.logo]);
    const Information = useMemo(() => {
        if (!blocks.information)
            return null;
        return (_jsx(BlockStyle, Object.assign({ fontSize: 'md', p: '4', style_id: blocks.information.style_id, w: '100%' }, { children: _jsx(Text, { children: t(blocks.information.text) }) })));
    }, [blocks.information]);
    const PaymentInfo = useMemo(() => {
        if (!blocks.paymentInfo || !nmibleStatus || nmibleStatus.payment_setup_complete === true)
            return null;
        const { button_style_id, button_text, instruction_text, title } = blocks.paymentInfo;
        const button_style = getStyleConfig(button_style_id);
        return (_jsxs(VStack, Object.assign({ align: 'start', gridGap: '6', p: '4', w: '100%', border: '2px solid', borderColor: 'darkBrandBlue.90', borderRadius: 'lg', shadow: 'lg' }, { children: [_jsxs(VStack, Object.assign({ align: 'start', gridGap: '2' }, { children: [_jsx(Heading, Object.assign({ as: 'h3', fontSize: 'lg', color: 'darkBrandBlue.90' }, { children: t(title) })), _jsx(MarkdownBase, { color: 'darkBrandBlue.40', markdown: t(instruction_text) })] })), _jsx(Button, Object.assign({ w: '100%', styles: button_style, onClick: () => navigate(ROUTE.PAYMENT_SETUP) }, { children: t(button_text) }))] })));
    }, [blocks.paymentInfo, nmibleStatus]);
    const ListItems = useMemo(() => {
        var _a;
        return (((_a = blocks.list.items) === null || _a === void 0 ? void 0 : _a.length) > 0 && (_jsxs(VStack, Object.assign({ alignItems: 'flex-start', w: '100%' }, { children: [_jsx(ConfigInlineHeader, Object.assign({}, blocks.list.header)), blocks.list.items.map((item) => {
                    return _jsx(ConfigBlockListItem, Object.assign({}, item), item.destination_id);
                })] }))));
    }, [blocks.list.items, blocks.list.header]);
    const Tiles = useMemo(() => {
        var _a, _b, _c;
        const mapNavigationDestination = (id) => {
            const destination = getNavigationDestinationConfig(id);
            if (destination.type === APP_CONFIG.NAVIGATION.NavigationType.APP_DESTINATION) {
                return {
                    path: '',
                    requiresAuth: true,
                    type: 'url:path'
                };
            }
            const request = destination.request;
            return Object.assign(Object.assign({}, request), { type: 'url:external', url: resolveWebViewUrl(request) });
        };
        const gridBlock = findConfigBlock(ConfigBlockType.GRID);
        const tiles = (_a = gridBlock === null || gridBlock === void 0 ? void 0 : gridBlock.tiles) !== null && _a !== void 0 ? _a : [];
        const transformTile = (tile) => ({
            backgroundImage: getImageConfig(tile === null || tile === void 0 ? void 0 : tile.background_image_id),
            icon: '',
            id: uuid_v4(),
            image_id: tile === null || tile === void 0 ? void 0 : tile.image_id,
            label: tile.text,
            phase_uuid: tile === null || tile === void 0 ? void 0 : tile.phases,
            request: mapNavigationDestination(tile === null || tile === void 0 ? void 0 : tile.destination_id),
            styleId: tile === null || tile === void 0 ? void 0 : tile.style_id,
            type: 'tile_grid_block'
        });
        // filter tiles based on the current user's phase.
        const filterByPhase = (tile) => !tile.phase_uuid || tile.phase_uuid.includes(currentUserPhase);
        // Process the main tile list.
        const tileList = tiles.map(transformTile).filter(filterByPhase);
        // Process the web tiles.
        const webTileSection = (_b = dashboardWeb === null || dashboardWeb === void 0 ? void 0 : dashboardWeb.items) === null || _b === void 0 ? void 0 : _b.find((item) => item.type === 'tile_grid_block');
        const webTiles = ((_c = webTileSection === null || webTileSection === void 0 ? void 0 : webTileSection.items) !== null && _c !== void 0 ? _c : [])
            .map((item) => (Object.assign(Object.assign({}, item), { backgroundImage: '', id: uuid_v4(), image_id: '', styleId: '' })))
            .filter(filterByPhase);
        // Combine Both tiles - web and mobiles.
        const allTiles = [...tileList, ...webTiles];
        const allowedUrlTypes = ['url:path', 'url:external'];
        if ((allTiles === null || allTiles === void 0 ? void 0 : allTiles.length) === 0)
            return null;
        return (_jsx(Flex, Object.assign({ gridGap: '8', textAlign: 'center', wrap: 'wrap' }, { children: allTiles.map(({ backgroundImage, icon, id, image_id, label, request, styleId }) => {
                return (allowedUrlTypes.includes(request.type) && (_jsxs(VStack, Object.assign({ alignItems: 'center', gridGap: '0.5', justifyContent: 'start' }, { children: [_jsx(NavLink, Object.assign({ "data-test": id }, (request.type === 'url:path'
                            ? {
                                to: request.path
                            }
                            : {
                                rel: 'noreferrer',
                                target: '_blank',
                                to: 'url' in request ? request.url : ''
                            }), { children: _jsx(BlockStyle, Object.assign({ bg: 'darkBrandBlue.5', style_id: styleId }, { children: _jsx(HStack, Object.assign({ backgroundImage: backgroundImage, backgroundRepeat: 'no-repeat', backgroundSize: '173px 125px', borderRadius: '8px', h: '125px', justifyContent: 'center', w: '173px' }, { children: icon && _jsx(Image, { alt: t(label), image_id: image_id, path: icon, w: '48px' }) })) })) }), id), _jsx(Text, Object.assign({ fontSize: 'large', w: '173px' }, { children: t(label) }))] }))));
            }) })));
    }, [dashboardWeb, getImageConfig, getNavigationDestinationConfig, currentUserPhase]);
    if (!dashboard)
        return _jsx(_Fragment, {});
    return (_jsx(VStack, { children: _jsxs(Flex, Object.assign({ flexDirection: 'column', gridGap: '12', maxW: '820px', px: '4', py: '16', w: '100%' }, { children: [_jsxs(VStack, Object.assign({ spacing: '4' }, { children: [StudyLogo, _jsx(Heading, Object.assign({ as: 'h1', fontSize: '2xl', textAlign: 'center' }, { children: t(dashboard === null || dashboard === void 0 ? void 0 : dashboard.title) })), Information] })), _jsxs(VStack, Object.assign({ spacing: '6', w: '100%' }, { children: [_jsx(ConfigSchedule, {}), ListItems] })), PaymentInfo, Tiles] })) }));
};
